import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { store as reduxStore } from "../index";
import api from "../services/backendService";
import utils from "../utils";
import { useSelector } from "react-redux";
import { setCustomers } from "../redux/actions/index";
import PropTypes from "prop-types"
import {
  findOwnOrganizationGuid,
  forgetStoredOrgFilter,
  isOrgFilterEnabledFor,
  readStoredOrgFilter,
  readStoredOrgFilterPaused,
  sameGuid,
  scopeOrganizations,
  storeOrgFilter,
  storeOrgFilterPaused,
} from "./orgFilter";
import {
  canFilterOnSite,
  forgetStoredSiteFilter,
  keepsSiteFilter,
  readStoredSiteFilter,
  readStoredSiteFilterPaused,
  restorableSiteFilter,
  storeSiteFilter,
  storeSiteFilterPaused,
  toSiteFilter,
} from "./siteFilter";
import { loadOrganizationAppConfigurationsAsync } from "../redux/reducers/organizationAppConfigurations";
import { selectOrganizationFlags } from "../redux/selectors/appConfigurations";

export const AuthorizationContext = createContext(null);

// The roles that can be assigned on each group: none that grants organization.write outside an
// organization. Shared by the load on sign-in and refreshGroups, so both give the same groups.
const withAssignableRoles = (groups) =>
  groups.map((group) => {
    if (group.type !== "Organization") {
      group.roles = group.roles.filter(
        (x) => !x.permissions.includes(Permissions.OrganizationWrite)
      );
    }
    return group;
  });

export const AuthorizationContextProvider = ({ children }) => {
  const [groups, setGroups] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [isReady, setIsReady] = useState(false);
  const [initError, setInitError] = useState(null);
  // The organization filter (contexts/orgFilter) replaces the selector:
  // decided at sign-in, false until then.
  const [isOrgFilterEnabled, setIsOrgFilterEnabled] = useState(false);
  // A paused filter: remembered, while the pages show every organization.
  const [pausedOrgGuid, setPausedOrgGuid] = useState(null);
  // What setOrgFilter looks the guid up in: callers may run right after an
  // initialize() (a new organization) and hold a context older than its list.
  const latestOrganizations = useRef([]);
  latestOrganizations.current = organizations;
  // The site filter (contexts/siteFilter), behind the same flag. The ref is
  // what setOrgFilter reads: a site page sets both filters in one commit.
  const [currentSiteFilter, setCurrentSiteFilter] = useState(null);
  const [isSiteFilterPaused, setIsSiteFilterPaused] = useState(false);
  const latestSiteFilter = useRef(null);
  // Decided once per session: initialize() runs again after many writes
  // (a fleet, a site, an organization) and must not flip the mode mid-session.
  const orgFilterDecision = useRef(null);

  const activeOrganization = useMemo(
    () => organizations.find((org) => org.isActive) || null,
    [organizations]
  );

  // Stable, so effects that depend on it do not re-run on every render.
  const isSuperAdmin = useCallback(() => reduxStore.getState().user.role === "Admin", []);

  // With the filter enabled, the active organization IS the filter: null
  // when there is no filter, i.e. when the pages show every organization.
  const filteredOrganization = isOrgFilterEnabled ? activeOrganization : null;
  const filteredOrgGuid = filteredOrganization?.guid ?? null;

  const user = () => reduxStore.getState().user;

  const activeOrgLocalStorageKey = "ao";

  // The remembered filter, unless it is paused: then no organization is active.
  const restoreFilterOrganization = (customers) => {
    const restoredGuid = restoreOrgFilter(customers);
    const isPaused = !!restoredGuid && readStoredOrgFilterPaused();
    setPausedOrgGuid(isPaused ? restoredGuid : null);
    return isPaused ? null : restoredGuid;
  };

  // The selector (flag off): the remembered organization, else the first one.
  const restoreSelectorOrganization = (customers) => {
    if (isSuperAdmin()) return null;
    const storedGuid = localStorage.getItem(activeOrgLocalStorageKey);
    if (customers.some((customer) => customer.customerGuid === storedGuid)) return storedGuid;
    if (customers.length === 0) return null;
    const firstGuid = customers[0].customerGuid;
    localStorage.setItem(activeOrgLocalStorageKey, firstGuid);
    return firstGuid;
  };

  const initialize = async () => {
    if (utils.isUnauthPath()) {
      setIsReady(true);
      return;
    }
    try {
      // IoT.API's memberships/me (AB#49868): core-IAM's groups, each with visible and, on sites,
      // siteUniqueId. None is left out, since the whole app reads them.
      const groups = withAssignableRoles(await api.getMyMemberships());

      setGroups(groups);

      const response = await api.getCustomersInitialState();
      reduxStore.dispatch(setCustomers(response));
      const customers = Object.values(response.byId);

      if (orgFilterDecision.current === null) {
        orgFilterDecision.current = await resolveOrgFilterEnabled(customers);
      } else if (orgFilterDecision.current && !isSuperAdmin()) {
        // A new organization has flags of its own: only those are asked.
        await loadOrganizationFlags(customers, selectOrganizationFlags(reduxStore.getState())).catch(
          (error) => utils.debug(error, utils.MSG_TYPE_ERR)
        );
      }
      const orgFilterEnabled = orgFilterDecision.current;

      const activeOrgGuid = orgFilterEnabled
        ? restoreFilterOrganization(customers)
        : restoreSelectorOrganization(customers);

      restoreSiteFilter({
        orgFilterEnabled,
        groups,
        filteredOrgGuid: orgFilterEnabled ? activeOrgGuid : null,
      });

      const orgs = customers.map((customer) => {
        const isActive =
          (orgFilterEnabled || !isSuperAdmin()) && customer.customerGuid === activeOrgGuid;
        return {
          guid: customer.customerGuid,
          name: customer.name,
          id: groups.find((g) => g.guid === customer.customerGuid)?.id,
          isActive,
          subscription: {
            activeFrom: customer.license?.activeFrom,
            duration: customer.license?.duration,
            features: customer.license
              ? JSON.parse(customer.license.content).features
              : {},
            licenseGuid: customer.license?.licenseGuid,
            name: customer.license?.name,
          },
        };
      });

      latestOrganizations.current = orgs;
      setOrganizations(orgs);
      setIsOrgFilterEnabled(orgFilterEnabled);

      setIsReady(true);
    } catch (error) {
      utils.debug("error", error);
      throw error;
    }
  };

  // The filter's flag is per organization, so every organization's flags are
  // loaded first. A super admin's flags are forced on in the UI, so they
  // always have the filter and none is loaded. Any failure: disabled.
  const resolveOrgFilterEnabled = async (customers) => {
    try {
      if (isSuperAdmin()) return isOrgFilterEnabledFor({ isSuperAdmin: true });
      if (customers.length === 0) return false;
      const { byOrg } = await loadOrganizationFlags(customers);
      const organizationGuids = customers.map((customer) => customer.customerGuid);
      return isOrgFilterEnabledFor({ isSuperAdmin: false, organizationGuids, flagsByOrg: byOrg });
    } catch (error) {
      utils.debug(error, utils.MSG_TYPE_ERR);
      return false;
    }
  };

  const loadOrganizationFlags = (customers, loaded) =>
    loadOrganizationAppConfigurationsAsync(
      reduxStore.dispatch,
      customers.map((customer) => customer.customerGuid),
      loaded
    );

  // The remembered filter, dropped when the user lost that organization.
  const restoreOrgFilter = (customers) => {
    const storedGuid = readStoredOrgFilter();
    const orgGuid = findOwnOrganizationGuid(
      storedGuid,
      customers.map((customer) => ({ guid: customer.customerGuid }))
    );
    if (storedGuid && !orgGuid) forgetStoredOrgFilter();
    return orgGuid;
  };

  // Kept in state, storage and the ref alike.
  const applySiteFilter = (next, isPaused = false) => {
    latestSiteFilter.current = next;
    storeSiteFilter(next);
    storeSiteFilterPaused(!!next && isPaused);
    setCurrentSiteFilter(next);
    setIsSiteFilterPaused(!!next && isPaused);
  };

  // The remembered site filter, dropped when the user lost the site or when
  // it is outside the restored organization filter. Not touched with the flag
  // off, so it is still there if the flag comes back.
  const restoreSiteFilter = ({ orgFilterEnabled, groups, filteredOrgGuid }) => {
    if (!orgFilterEnabled) return;
    const stored = readStoredSiteFilter();
    const restored = restorableSiteFilter({
      stored,
      groups,
      isSuperAdmin: isSuperAdmin(),
      filteredOrgGuid,
    });
    if (stored && !restored) forgetStoredSiteFilter();
    applySiteFilter(restored, !!restored && readStoredSiteFilterPaused());
  };

  // Reloads the groups from memberships/me, for Access Rights when it opens: what it lists must be
  // current, and the sign-in load may be hours old. Leaves them as they are while the token is not
  // ready (null) and when the call fails, which it rethrows for the caller to handle.
  const refreshGroups = async () => {
    const groups = await api.getMyMemberships();
    if (groups === null) return null;
    const assignable = withAssignableRoles(groups);
    setGroups(assignable);
    return assignable;
  };

  // The load on sign-in. initialize() rethrows for the callers that re-run it
  // after a change and handle the failure themselves; here nobody would: the
  // rejection would go unhandled and isReady stay false, leaving the app on its
  // preload screen. Keep the failure for the app to show, with a retry.
  const loadOnSignIn = async () => {
    setInitError(null);
    try {
      await initialize();
    } catch (error) {
      setInitError(error);
    }
  };

  const isUserLoaded = useSelector((state) => state.user?.isLoaded);

  React.useEffect(() => {
    if (isUserLoaded) {
      void loadOnSignIn();
    }
  }, [isUserLoaded]);

  // Sets the filter, or clears it with null. A guid that is not one of the
  // user's organizations (a site reached through a site role only) leaves the
  // filter as it is. Refreshes the subscription, as choosing in the selector does.
  const setOrgFilter = (orgGuid) => {
    if (!isOrgFilterEnabled) return;
    const target = orgGuid
      ? latestOrganizations.current.find((org) => sameGuid(org.guid, orgGuid))
      : null;
    if (orgGuid && !target) return;
    // Already the filter (a paused one is not): nothing to change or reload.
    if (target && sameGuid(target.guid, filteredOrgGuid)) return;
    // Another organization, or none: a site filter outside it goes.
    const currentSite = latestSiteFilter.current;
    if (currentSite && !keepsSiteFilter(currentSite, target?.guid)) applySiteFilter(null);
    storeOrgFilter(target?.guid ?? null);
    storeOrgFilterPaused(false);
    setPausedOrgGuid(null);
    setOrganizations((prevOrgs) =>
      prevOrgs.map((org) => ({ ...org, isActive: !!target && org.guid === target.guid }))
    );
    if (target) updateOrganizationSubscription(target.guid).catch(() => {});
  };
  const clearOrgFilter = () => setOrgFilter(null);

  // Shows every organization without forgetting the filter (the sidebar chip).
  const pauseOrgFilter = () => {
    if (!filteredOrgGuid) return;
    storeOrgFilterPaused(true);
    setPausedOrgGuid(filteredOrgGuid);
    setOrganizations((prevOrgs) => prevOrgs.map((org) => ({ ...org, isActive: false })));
  };
  const resumeOrgFilter = () => {
    if (pausedOrgGuid) setOrgFilter(pausedOrgGuid);
  };

  // Opening a site page sets the site filter on it (`orgGuid`: the
  // organization owning it). Nothing for a site the user sees only through a
  // fleet. Set after the organization filter (usePageOrganization), so
  // that change, which drops a site of another organization, keeps this one.
  const setSiteFilter = (site, orgGuid = null) => {
    if (!isOrgFilterEnabled) return;
    const next = toSiteFilter(site, orgGuid);
    if (!next) return;
    if (!canFilterOnSite({ siteGuid: next.siteGuid, groups, isSuperAdmin: isSuperAdmin() })) return;
    // Already the filter, as it is (a paused one is not): nothing to write.
    if (!isSiteFilterPaused && JSON.stringify(latestSiteFilter.current) === JSON.stringify(next)) return;
    applySiteFilter(next);
  };
  const clearSiteFilter = () => applySiteFilter(null);
  // Highlights nothing without forgetting the site (the sidebar badge).
  const pauseSiteFilter = () => {
    if (latestSiteFilter.current) applySiteFilter(latestSiteFilter.current, true);
  };
  const resumeSiteFilter = () => {
    if (latestSiteFilter.current) applySiteFilter(latestSiteFilter.current, false);
  };

  const setActiveOrganization = async (orgGuid) => {
    if (isOrgFilterEnabled) {
      setOrgFilter(orgGuid);
      return;
    }
    setOrganizations((prevOrgs) =>
      prevOrgs.map((org) => ({
        ...org,
        isActive: org.guid === orgGuid,
      }))
    );
    await updateOrganizationSubscription(orgGuid);
    localStorage.setItem(activeOrgLocalStorageKey, orgGuid);
  };

  const setOrganizationSubscription = async (orgGuid, subscriptionData) => {
    setOrganizations((prevOrgs) =>
      prevOrgs.map((org) => ({
        ...org,
        subscription:
          org.guid === orgGuid ? subscriptionData : org.subscription,
      }))
    );
  };

  const updateOrganizationSubscription = async (orgGuid) => {
    if (!isSuperAdmin()) {
      try {
        const subscriptionResponse = await api.getCustomerSubscription(orgGuid);
        await setOrganizationSubscription(orgGuid, subscriptionResponse.data);
        return subscriptionResponse.data;
      } catch (error) {
        utils.debug(error, utils.MSG_TYPE_ERR);
        throw error;
      }
    }
    return null;
  };

  const getGroupByGuid = (groupGuid) => {
    const iamGroup = groups.find((group) => group.guid === groupGuid);
    if (iamGroup) {
      return { groupId: iamGroup.id };
    }

    return null;
  };

  const isMemberOfSomeOrganization = () =>
    isSuperAdmin() || groups.some((group) => group.type === "Organization");

  const canRegisterNewGateway = () => isMemberOfSomeOrganization();

  const hasPermission = (groupGuid, permission) => {
    if (isSuperAdmin()) {
      return true;
    }

    const groupData = getGroupByGuid(groupGuid);

    if (!groupData) {
      return false;
    }

    const group = groups.find((g) => g.id === groupData.groupId);
    return group ? group.role.permissions.includes(permission) : false;
  };

  // --- Organization filter scope -------------------------------------------
  // An option is offered when one organization in scope allows it, and then
  // acts only on the organizations that allow it (the *Of / *For helpers).

  const findOrganization = (orgGuid) =>
    organizations.find((org) => sameGuid(org.guid, orgGuid)) || null;
  const pausedOrganization = pausedOrgGuid ? findOrganization(pausedOrgGuid) : null;
  const filteredSite = isOrgFilterEnabled && !isSiteFilterPaused ? currentSiteFilter : null;
  const pausedSite = isOrgFilterEnabled && isSiteFilterPaused ? currentSiteFilter : null;

  const superAdmin = isSuperAdmin();
  const organizationsInScope = useMemo(() => {
    if (isOrgFilterEnabled) return scopeOrganizations(organizations, filteredOrgGuid);
    if (superAdmin) return organizations;
    return activeOrganization ? [activeOrganization] : [];
  }, [isOrgFilterEnabled, organizations, filteredOrgGuid, superAdmin, activeOrganization]);

  const canSeeHiddenSitesOf = (orgGuid) => {
    if (isSuperAdmin()) return true;
    const org = findOrganization(orgGuid);
    return !!org && hasPermissions(org.guid, [Permissions.GroupRead, Permissions.SiteHiddenRead]);
  };

  const isPaidOrganization = (orgGuid) => {
    if (isSuperAdmin()) return true;
    const org = findOrganization(orgGuid);
    return !!org && String(org.subscription?.name).toLowerCase() !== "essential";
  };

  const hasMapViewFor = (orgGuid) =>
    isSuperAdmin() || findOrganization(orgGuid)?.subscription?.features?.mapView === true;

  const userCanSeeHiddenSites = () => {
    if (isOrgFilterEnabled) {
      return organizationsInScope.some((org) => canSeeHiddenSitesOf(org.guid));
    }
    return (
      isSuperAdmin() ||
      (activeOrganization &&
        hasPermissions(activeOrganization.guid, [
          Permissions.GroupRead,
          Permissions.SiteHiddenRead,
        ]))
    );
  };

  const getSitesWithWritePermission = () => {
    const writableGuids = new Set(
      groups
        .filter(
          (group) =>
            group.type === "Site" &&
            (isSuperAdmin() ||
              group.role.permissions.includes(Permissions.GroupWrite))
        )
        .map((group) => group.guid)
    );
    const { sites } = reduxStore.getState();
    return Object.values(sites.byId).filter((site) =>
      writableGuids.has(site.siteGuid)
    );
  };

  const hasWritePermission = (groupGuid) =>
    hasPermission(groupGuid, Permissions.GroupWrite);

  // Same rule as IoT.API ResourceAccessScopeService: a device-level permission is granted
  // by the device's fleet or, failing that, by its site.
  const hasDevicePermission = (siteGuid, fleetGuid, permission) =>
    isSuperAdmin() ||
    (!!fleetGuid && hasPermission(fleetGuid, permission)) ||
    (!!siteGuid && hasPermission(siteGuid, permission));

  const hasPermissions = (groupGuid, permissions) => {
    if (isSuperAdmin()) {
      return true;
    }

    const permissionsArray = Array.isArray(permissions)
      ? permissions
      : [permissions];

    const groupData = getGroupByGuid(groupGuid);
    if (!groupData) {
      return false;
    }

    const group = groups.find((g) => g.id === groupData.groupId);
    if (!group) {
      return false;
    }

    return permissionsArray.every((permission) =>
      group.role.permissions.includes(permission)
    );
  };

  const isOrgManagementEnabled = (groupGuid) => {
    if (isSuperAdmin() || groupGuid === null) {
      return true;
    }

    const subscription = organizations.find(
      (org) => org.guid === groupGuid
    )?.subscription;

    return (
      hasPermission(groupGuid, Permissions.OrganizationWrite) &&
      subscription?.features.organizationManagement
    );
  };

  const getOrganizationId = (groupId) => {
    const group = groups.find((g) => g.id === groupId);
    if (!group) {
      return null;
    }

    if (group.type === "Organization") {
      return group.id;
    }

    return getOrganizationId(group.parentGroupId);
  };

  const getOrganizationGuid = (groupGuid) => {
    const group = groups.find((g) => g.guid === groupGuid);
    if (!group) {
      return null;
    }

    if (group.type === "Organization") {
      return group.guid;
    }

    const parentGroup = groups.find((g) => g.id === group.parentGroupId);
    if (parentGroup.type === "Organization") {
      return parentGroup.guid;
    }

    return null;
  };

  const getSubscriptionFeatureValue = (featureName, groupGuid = null) => {
    const group = groups.find(
      (g) => g.guid === (groupGuid ?? activeOrganization?.guid)
    );
    if (!group) {
      return null;
    }
    const organization =
      group.type === "Site" || group.type === "Fleet"
        ? organizations.find(
            (org) => org.id === getOrganizationId(group.parentGroupId)
          )
        : organizations.find((org) => org.id === group.id);

    const subscription = organization?.subscription;

    if (!subscription?.features) {
      return null;
    }
    return subscription.features[featureName] ?? null;
  };

  const getSubscriptionName = (groupGuid = null) => {
    const group = groups.find(
      (g) => g.guid === (groupGuid ?? activeOrganization?.guid)
    );
    if (!group) {
      return null;
    }
    const organization =
      group.type === "Site"
        ? organizations.find(
            (org) => org.id === getOrganizationId(group.parentGroupId)
          )
        : organizations.find((org) => org.id === group.id);

    const subscription = organization?.subscription;

    return subscription?.name || null;
  };

  const isPaidSubscription = () => {
    if (isSuperAdmin()) {
      return true;
    }
    if (isOrgFilterEnabled) {
      return organizationsInScope.some((org) => isPaidOrganization(org.guid));
    }
    const orgGuid = activeOrganization?.guid;
    if (!orgGuid) {
      return false;
    }
    return String(getSubscriptionName(orgGuid)).toLowerCase() !== "essential";
  };

  // Same rule as the xp-auditlog export: AuditRead on the active organization and a Full
  // subscription (the export requires both, unless super admin).
  const canViewAuditLogFor = (orgGuid) => {
    if (isSuperAdmin()) {
      return true;
    }
    const org = findOrganization(orgGuid);
    return (
      !!org &&
      getSubscriptionName(org.guid) === "Full" &&
      hasPermission(org.guid, Permissions.AuditRead)
    );
  };

  const canViewAuditLog = () => {
    if (isSuperAdmin()) {
      return true;
    }
    // The entry is there when an organization in scope allows it: the
    // filtered one, or any with no filter.
    if (isOrgFilterEnabled) {
      return organizationsInScope.some((org) => canViewAuditLogFor(org.guid));
    }
    if (!activeOrganization) {
      return false;
    }
    if (getSubscriptionName(activeOrganization.guid) !== "Full") {
      return false;
    }
    return hasPermission(activeOrganization.guid, Permissions.AuditRead);
  };

  const contextValue = useMemo(
    () => ({
      user,
      initialize,
      organizations,
      setOrganizations,
      setActiveOrganization,
      activeOrganization,
      isSuperAdmin,
      setOrganizationSubscription,
      updateOrganizationSubscription,
      groups,
      setGroups,
      refreshGroups,
      isMemberOfSomeOrganization,
      canRegisterNewGateway,
      hasPermission,
      hasWritePermission,
      hasDevicePermission,
      hasPermissions,
      isOrgManagementEnabled,
      getSitesWithWritePermission,
      getSubscriptionFeatureValue,
      getSubscriptionName,
      userCanSeeHiddenSites,
      isReady,
      initError,
      retryInitialize: loadOnSignIn,
      isPaidSubscription,
      getOrganizationGuid,
      canViewAuditLog,
      isOrgFilterEnabled,
      filteredOrganization,
      filteredOrgGuid,
      setOrgFilter,
      clearOrgFilter,
      pausedOrganization,
      pauseOrgFilter,
      resumeOrgFilter,
      organizationsInScope,
      findOrganization,
      canSeeHiddenSitesOf,
      isPaidOrganization,
      hasMapViewFor,
      canViewAuditLogFor,
      filteredSite,
      pausedSite,
      setSiteFilter,
      clearSiteFilter,
      pauseSiteFilter,
      resumeSiteFilter,
    }),
    [
      user,
      initialize,
      organizations,
      setOrganizations,
      setActiveOrganization,
      activeOrganization,
      isSuperAdmin,
      setOrganizationSubscription,
      updateOrganizationSubscription,
      groups,
      setGroups,
      refreshGroups,
      isMemberOfSomeOrganization,
      canRegisterNewGateway,
      hasPermission,
      hasWritePermission,
      hasDevicePermission,
      hasPermissions,
      isOrgManagementEnabled,
      getSitesWithWritePermission,
      getSubscriptionFeatureValue,
      getSubscriptionName,
      userCanSeeHiddenSites,
      isReady,
      initError,
      loadOnSignIn,
      isPaidSubscription,
      getOrganizationGuid,
      canViewAuditLog,
      isOrgFilterEnabled,
      filteredOrganization,
      filteredOrgGuid,
      setOrgFilter,
      clearOrgFilter,
      pausedOrganization,
      pauseOrgFilter,
      resumeOrgFilter,
      organizationsInScope,
      findOrganization,
      canSeeHiddenSitesOf,
      isPaidOrganization,
      hasMapViewFor,
      canViewAuditLogFor,
      filteredSite,
      pausedSite,
      setSiteFilter,
      clearSiteFilter,
      pauseSiteFilter,
      resumeSiteFilter,
    ]
  );

  return (
    <AuthorizationContext.Provider value={contextValue}>
      {children}
    </AuthorizationContext.Provider>
  );
};

export function useAuthorizationContext() {
  const context = useContext(AuthorizationContext);
  if (!context) {
    throw new Error(
      "useAuthorizationContext must be used within a AuthorizationContextProvider"
    );
  }
  return context;
}

export class Permissions {
  static GroupRead = "group.read";
  static GroupWrite = "group.write";
  static GroupUsersRead = "group.users.read";
  static GroupUsersWrite = "group.users.write";
  static GroupRolesWrite = "group.roles.write";
  static OrganizationWrite = "organization.write";
  static SiteHiddenRead = "site.hidden.read";
  static SiteHiddenWrite = "site.hidden.write";
  static FleetAssetWrite = "fleet.asset.write";
  static FleetAssetDelete = "fleet.asset.delete";
  static GroupDevicesOwnership = "group.devices.ownership";
  static MqttRead = "mqtt.read";
  static MqttWrite = "mqtt.write";
  static ControllerParametersRead = "controller.parameters.read";
  static ControllerParametersWrite = "controller.parameters.write";
  static ControllerVariablesWrite = "controller.variables.write";
  static AlertRead = "alert.read";
  static AlertWrite = "alert.write";
  static AuditRead = "audit.read";
  static TemplateRead = "template.read";
  static TemplateWrite = "template.write";
}

AuthorizationContextProvider.propTypes = {
  children: PropTypes.node,
};